// /accounts/.../secrets/$secretId — metadata-only detail. The backend
// GET endpoint exists but the response shape is identical to the
// list row (no plaintext anywhere). This page exists so URLs are
// shareable and the operator can confirm a specific secret by id.

import { useQuery } from "@tanstack/react-query";

import { tenantSecrets } from "../../api/endpoints/tenantSecrets";
import { adminQueryOptions } from "../../api/adminQueryOptions";
import { qk } from "../../api/queryKeys";
import { CopyButton } from "../../components/ui/CopyButton";
import { ErrorBanner } from "../../components/ui/ErrorBanner";
import { Pill } from "../../components/ui/Pill";
import { PageHeader } from "../../components/shell/PageHeader";

interface TenantSecretDetailPageProps {
  accountSlug: string;
  tenantSlug: string;
  secretId: string;
}

export function TenantSecretDetailPage({
  accountSlug,
  tenantSlug,
  secretId,
}: TenantSecretDetailPageProps) {
  const q = useQuery({
    ...adminQueryOptions,
    queryKey: qk.tenantSecret(accountSlug, tenantSlug, secretId),
    queryFn: () =>
      tenantSecrets.get(accountSlug, tenantSlug, secretId).then((r) => r.data),
  });

  return (
    <>
      <PageHeader
        title={q.data?.ref ?? "Secret"}
        subtitle={
          <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            <code style={{ fontSize: "0.8rem" }}>{secretId}</code>
            <CopyButton value={secretId} label="Copy id" />
          </span>
        }
      />
      {q.isPending && <p style={{ color: "var(--text-dim)" }}>Loading…</p>}
      {q.error && <ErrorBanner error={q.error} title="Failed to load secret" />}
      {q.data && (
        <section style={card}>
          <dl style={dl}>
            <dt>Ref</dt>
            <dd><code>{q.data.ref}</code></dd>
            <dt>Tenant id</dt>
            <dd><code style={{ fontSize: "0.78rem" }}>{q.data.tenant_id}</code></dd>
            <dt>KEK version</dt>
            <dd>
              <Pill tone="neutral">v{q.data.encryption_key_version}</Pill>
            </dd>
          </dl>
        </section>
      )}
    </>
  );
}

const card: React.CSSProperties = {
  background: "var(--surface)",
  border: "1px solid var(--border)",
  borderRadius: 6,
  padding: 16,
};

const dl: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "max-content 1fr",
  gap: "6px 16px",
  margin: 0,
  fontSize: "0.9rem",
};
